import { state } from '../store';
import { api } from '../api/client';
import { currentPlayers } from '../screens/GameSetup';
import { t } from '../i18n';
import { safeStorage } from '../utils/storage';
import { shuffle } from '../utils/random';
import type {
  MinigameId,
  TournamentSettings,
  TournamentState,
  TournamentTeam,
  MinigamePool,
} from '../types/tournament';
import { TEAM_DEFAULTS } from '../types/tournament';
import type { UnlockType } from '../types/content';

const LS_SETTINGS = 'pg_tournament_settings';
const LS_ACTIVE   = 'pg_tournament_active';

// ─── localStorage ───────────────────────────────────────────────────

const ALL_CATS: UnlockType[] = ['free','coin','premium','invite'];

export function loadSavedSettings(): TournamentSettings {
  try {
    const raw = safeStorage.get(LS_SETTINGS);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') {
        return {
          scoreTarget: [5,10,15,20].includes(parsed.scoreTarget) ? parsed.scoreTarget : 10,
          turnDurationSec: [0,30,60,90].includes(parsed.turnDurationSec) ? parsed.turnDurationSec : 60,
          punishment: typeof parsed.punishment === 'string' ? parsed.punishment.slice(0,200) : '',
          enabledMinigames: Array.isArray(parsed.enabledMinigames) && parsed.enabledMinigames.length > 0
            ? parsed.enabledMinigames.filter((x: any) => ['wai','describe','act','emoji'].includes(x))
            : ['wai','describe','act'],
          enabledCategories: Array.isArray(parsed.enabledCategories) && parsed.enabledCategories.length > 0
            ? parsed.enabledCategories.filter((x: any) => ALL_CATS.includes(x))
            : [...ALL_CATS],
        };
      }
    }
  } catch (_) {}
  return {
    scoreTarget: 10,
    turnDurationSec: 60,
    punishment: '',
    enabledMinigames: ['wai','describe','act'],
    enabledCategories: [...ALL_CATS],
  };
}

export function saveSettings(s: TournamentSettings): void {
  safeStorage.set(LS_SETTINGS, JSON.stringify(s));
}

export function persistActive(): void {
  if (!state.tournament) {
    safeStorage.remove(LS_ACTIVE);
    return;
  }
  safeStorage.set(LS_ACTIVE, JSON.stringify(state.tournament));
}

export function tryRestoreActive(): boolean {
  try {
    const raw = safeStorage.get(LS_ACTIVE);
    if (!raw) return false;
    const parsed = JSON.parse(raw) as TournamentState;
    if (!parsed || !parsed.teams || !parsed.settings) return false;
    // Backfill new fields for old saved states
    parsed.suddenDeathModalShown = parsed.suddenDeathModalShown ?? false;
    parsed.suddenDeathRound = parsed.suddenDeathRound ?? 0;
    parsed.lastRoundSnapshot = parsed.lastRoundSnapshot ?? null;
    state.tournament = parsed;
    return true;
  } catch (_) {
    return false;
  }
}

export function clearActive(): void {
  state.tournament = null;
  safeStorage.remove(LS_ACTIVE);
}

// ─── Teams ──────────────────────────────────────────────────────────

export function makeDefaultTeams(count: number, tt: (k: string) => string): TournamentTeam[] {
  const n = Math.max(2, Math.min(4, count));
  const teams: TournamentTeam[] = [];
  for (let i = 0; i < n; i++) {
    teams.push({
      id: 't' + (i + 1),
      name: tt('trn_setup_team_default').replace('{n}', String(i + 1)),
      emoji: TEAM_DEFAULTS[i].emoji,
      color: TEAM_DEFAULTS[i].color,
      score: 0,
      playerIds: [],
    });
  }
  return teams;
}

export function autoSplitPlayers(teams: TournamentTeam[]): TournamentTeam[] {
  const allIds = currentPlayers.filter(p => p.name.trim()).map(p => p.id);
  const next = teams.map(t => ({ ...t, playerIds: [] as string[] }));
  allIds.forEach((id, i) => { next[i % next.length].playerIds.push(id); });
  return next;
}

export function unassignedPlayers(teams: TournamentTeam[]): typeof currentPlayers {
  const assigned = new Set<string>();
  teams.forEach(t => t.playerIds.forEach(id => assigned.add(id)));
  return currentPlayers.filter(p => p.name.trim() && !assigned.has(p.id));
}

// ─── Card pool loading ──────────────────────────────────────────────

/**
 * Загружает карточки для всех включённых мини-игр.
 * - Учитывает enabledCategories (типы unlock_type для уровней).
 * - Берёт ВСЕ доступные пользователю уровни из этих категорий.
 * - Грузит и перемешивает все карточки в один пул на каждую мини-игру.
 */
export async function loadMinigamePool(
  enabled: MinigameId[],
  enabledCategories: UnlockType[],
): Promise<MinigamePool> {
  const lang = state.user?.lang || 'en';
  const levelsByGame = state.levels;

  // 1. Гарантируем что уровни всех нужных мини-игр загружены.
  const need: MinigameId[] = [];
  for (const id of enabled) {
    if (!levelsByGame[id] || levelsByGame[id]!.length === 0) need.push(id);
  }
  if (need.length > 0) {
    await Promise.all(need.map(async (id) => {
      try {
        const res: any = await api.getLevels(id);
        state.levels[id] = res.levels ?? res;
      } catch (e) {
        console.warn('Failed to load levels for', id, e);
        state.levels[id] = [];
      }
    }));
  }

  // 2. Собираем все (gameId, levelId) пары со всех включённых игр.
  const items: Array<{ gameId: string; levelId: string }> = [];
  const gameToLevels = new Map<MinigameId, string[]>();

  for (const id of enabled) {
    const allLevels = state.levels[id] || [];
    const filtered = allLevels.filter(l =>
      enabledCategories.includes(l.unlock_type as UnlockType) && l.user_has_access
    );
    if (filtered.length === 0) continue;
    gameToLevels.set(id, filtered.map(l => l.id));
    for (const lvl of filtered) {
      items.push({ gameId: id, levelId: lvl.id });
    }
  }

  if (items.length === 0) return {};

  const sid = Math.random().toString(36).slice(2, 10);
  let bulkResults: Array<import('../api/client').BulkResultItem> = [];
  try {
    const res = await api.getCardsBulk(items, lang, null, sid);
    bulkResults = res.results;
  } catch (e) {
    console.warn('Failed to load bulk cards', e);
    return {};
  }

  const result: MinigamePool = {};

  for (const id of enabled) {
    const levelIds = gameToLevels.get(id);
    if (!levelIds) continue;

    const itemsForGame = bulkResults.filter(r => r.gameId === id && !r.error);
    if (itemsForGame.length === 0) continue;

    const merged: string[] = itemsForGame.flatMap(r =>
      r.card_type === 'tod'
        ? (r.cards as Array<{ type: string; text: string }>).map(c => c.text)
        : r.cards as string[]
    );
    if (merged.length === 0) continue;

    result[id] = {
      levelIds: itemsForGame.map(r => r.levelId),
      cards: shuffle(merged),
      cursor: 0,
    };
  }

  return result;
}

// ─── Tournament lifecycle ───────────────────────────────────────────

export function startTournament(
  teams: TournamentTeam[],
  settings: TournamentSettings,
  pool: MinigamePool,
): void {
  const activeTeams = teams.filter(t => t.playerIds.length > 0);
  const playerCursor: Record<string, number> = {};
  activeTeams.forEach(t => { playerCursor[t.id] = 0; });

  state.tournament = {
    teams: activeTeams,
    settings,
    pool,
    currentTeamIndex: 0,
    roundNumber: 1,
    currentMinigame: null,
    currentPlayerInTeam: playerCursor,
    status: 'lobby',
    isSuddenDeath: false,
    suddenDeathModalShown: false,
    suddenDeathRound: 0,
    winnerTeamId: null,
    finishedThisRound: [],
    lastRoundSnapshot: null,
  };
  saveSettings(settings);
  persistActive();
}

export function rollMinigame(): MinigameId | null {
  const tr = state.tournament;
  if (!tr) return null;
  const candidates = tr.settings.enabledMinigames
    .filter(id => (tr.pool[id]?.cards.length ?? 0) > 0);
  if (candidates.length === 0) return null;
  return candidates[Math.floor(Math.random() * candidates.length)];
}

export function getCurrentTeam(): TournamentTeam | null {
  const tr = state.tournament;
  if (!tr) return null;
  return tr.teams[tr.currentTeamIndex] ?? null;
}

export function getCurrentPlayerName(): { emoji: string; name: string } | null {
  const tr = state.tournament;
  const team = getCurrentTeam();
  if (!tr || !team || team.playerIds.length === 0) return null;
  const cursor = tr.currentPlayerInTeam[team.id] ?? 0;
  const playerId = team.playerIds[cursor % team.playerIds.length];
  const p = currentPlayers.find(pp => pp.id === playerId);
  return p ? { emoji: p.emoji, name: p.name } : null;
}

export function pickNextCard(mg: MinigameId): string | null {
  const tr = state.tournament;
  if (!tr) return null;
  const entry = tr.pool[mg];
  if (!entry || entry.cards.length === 0) return null;

  // Если прошли весь пул — перешафливаем.
  if (entry.cursor >= entry.cards.length) {
    entry.cards = shuffle(entry.cards);
    entry.cursor = 0;
  }
  const card = entry.cards[entry.cursor];
  entry.cursor += 1;
  return card;
}

export function setActiveMinigame(mg: MinigameId): void {
  if (!state.tournament) return;
  state.tournament.currentMinigame = mg;
  state.tournament.status = 'playing';
  persistActive();
}

const MAX_SD_ROUNDS = 5;

export function reportRoundResult(success: boolean): void {
  const tr = state.tournament;
  if (!tr) return;

  // Save snapshot before any modifications (enables undo)
  tr.lastRoundSnapshot = {
    teams: tr.teams.map(t => ({ ...t, playerIds: [...t.playerIds] })),
    currentTeamIndex: tr.currentTeamIndex,
    roundNumber: tr.roundNumber,
    isSuddenDeath: tr.isSuddenDeath,
    suddenDeathRound: tr.suddenDeathRound,
    settings: { ...tr.settings, enabledMinigames: [...tr.settings.enabledMinigames], enabledCategories: [...tr.settings.enabledCategories] },
    finishedThisRound: [...tr.finishedThisRound],
    currentPlayerInTeam: { ...tr.currentPlayerInTeam },
  };

  const team = getCurrentTeam();
  if (team && success) {
    team.score += 1;
    if (team.score >= tr.settings.scoreTarget) {
      tr.finishedThisRound.push(team.id);
    }
  }

  if (team && team.playerIds.length > 0) {
    const cur = tr.currentPlayerInTeam[team.id] ?? 0;
    tr.currentPlayerInTeam[team.id] = (cur + 1) % team.playerIds.length;
  }

  tr.currentTeamIndex = (tr.currentTeamIndex + 1) % tr.teams.length;
  tr.roundNumber += 1;
  tr.currentMinigame = null;

  // Track SD cycles: each full cycle (all teams played) = 1 SD round
  if (tr.isSuddenDeath && tr.currentTeamIndex === 0) {
    tr.suddenDeathRound += 1;
  }

  if (tr.finishedThisRound.length > 0 && tr.currentTeamIndex === 0) {
    if (tr.finishedThisRound.length === 1) {
      tr.winnerTeamId = tr.finishedThisRound[0];
      tr.status = 'finished';
    } else {
      const survivors = tr.teams.filter(t => tr.finishedThisRound.includes(t.id));
      const maxScore = Math.max(...survivors.map(s => s.score));
      tr.teams = survivors;
      tr.currentTeamIndex = 0;
      tr.status = 'lobby';
      tr.isSuddenDeath = true;
      tr.suddenDeathModalShown = false; // show modal again for new SD layer
      tr.suddenDeathRound = 0;
      tr.settings = { ...tr.settings, scoreTarget: (maxScore + 1) as any };
      tr.finishedThisRound = [];
    }
  } else if (tr.isSuddenDeath && tr.suddenDeathRound >= MAX_SD_ROUNDS && tr.currentTeamIndex === 0) {
    // SD time limit reached — pick winner by highest score
    const sorted = [...tr.teams].sort((a, b) => b.score - a.score);
    tr.winnerTeamId = sorted[0].id;
    tr.status = 'finished';
  } else {
    tr.status = 'lobby';
  }

  persistActive();
}

export function undoLastRound(): void {
  const tr = state.tournament;
  if (!tr || !tr.lastRoundSnapshot) return;
  const snap = tr.lastRoundSnapshot;
  tr.teams = snap.teams;
  tr.currentTeamIndex = snap.currentTeamIndex;
  tr.roundNumber = snap.roundNumber;
  tr.isSuddenDeath = snap.isSuddenDeath;
  tr.suddenDeathRound = snap.suddenDeathRound;
  tr.settings = snap.settings;
  tr.finishedThisRound = snap.finishedThisRound;
  tr.currentPlayerInTeam = snap.currentPlayerInTeam;
  tr.lastRoundSnapshot = null;
  tr.status = 'lobby';
  tr.currentMinigame = null;
  tr.winnerTeamId = null;
  persistActive();
  window.renderApp();
}

export function endTournament(): void {
  clearActive();
}

export function startRematch(): void {
  const tr = state.tournament;
  if (!tr) return;
  // Restore original settings (SD may have modified scoreTarget)
  const originalSettings = loadSavedSettings();
  tr.settings = originalSettings;
  tr.teams = tr.teams.map(t => ({ ...t, score: 0 }));
  tr.currentTeamIndex = 0;
  tr.roundNumber = 1;
  tr.currentMinigame = null;
  // Re-shuffle each pool & reset cursor → карточки идут в новом порядке!
  for (const key of Object.keys(tr.pool) as MinigameId[]) {
    const entry = tr.pool[key];
    if (entry) {
      entry.cards = shuffle(entry.cards);
      entry.cursor = 0;
    }
  }
  tr.currentPlayerInTeam = Object.fromEntries(tr.teams.map(t => [t.id, 0]));
  tr.status = 'lobby';
  tr.isSuddenDeath = false;
  tr.suddenDeathModalShown = false;
  tr.suddenDeathRound = 0;
  tr.winnerTeamId = null;
  tr.finishedThisRound = [];
  tr.lastRoundSnapshot = null;
  persistActive();
}

// ─── Exit confirm (общий, вызываемый из Board, из Timer/Emoji-движков, из tma back) ─

let _exitConfirmShown = false;

export function trnExitConfirm(): void {
  if (_exitConfirmShown) return;
  _exitConfirmShown = true;

  const overlay = document.createElement('div');
  overlay.id = 'trn-exit-overlay';
  overlay.style.cssText = 'position:fixed;inset:0;z-index:1000;background:rgba(0,0,0,0.7);backdrop-filter:blur(8px);display:flex;align-items:center;justify-content:center;padding:24px;';
  overlay.innerHTML = `
    <div style="background:linear-gradient(135deg,#13112A,#1A1836);border:1px solid rgba(255,45,107,0.3);border-radius:24px;padding:24px;text-align:center;max-width:320px;width:100%;">
      <div style="font-size:48px;margin-bottom:12px;">⚠️</div>
      <div style="font-family:Inter,sans-serif;font-size:20px;font-weight:800;color:#fff;margin-bottom:8px;">${t('trn_board_exit_title')}</div>
      <div style="color:#B0ADCF;font-size:13px;font-weight:600;margin-bottom:20px;">${t('trn_board_exit_text')}</div>
      <button id="trn-exit-yes" style="width:100%;padding:14px;background:linear-gradient(135deg,#FF2D6B,#A020F0);border:none;border-radius:9999px;color:white;font-size:15px;font-weight:800;cursor:pointer;margin-bottom:8px;">${t('trn_board_exit_confirm')}</button>
      <button id="trn-exit-no" style="width:100%;padding:12px;background:transparent;border:none;color:rgba(255,255,255,0.6);font-size:14px;font-weight:600;cursor:pointer;">${t('trn_board_exit_cancel')}</button>
    </div>
  `;
  const cleanup = () => { _exitConfirmShown = false; overlay.remove(); };
  overlay.addEventListener('click', (e) => { if (e.target === overlay) cleanup(); });
  document.body.appendChild(overlay);
  document.getElementById('trn-exit-no')?.addEventListener('click', cleanup);
  document.getElementById('trn-exit-yes')?.addEventListener('click', () => {
    cleanup();
    endTournament();
    window.navTo('home');
  });
}

// Глобально для tma.ts back-handler.
declare global {
  interface Window {
    trnExitConfirm: () => void;
    trnUndoRound: () => void;
  }
}
window.trnExitConfirm = trnExitConfirm;
window.trnUndoRound = undoLastRound;

declare global {
  interface Window {
    __isTrnPlay?: () => boolean;
  }
}
window.__isTrnPlay = () => !!state.tournament && state.tournament.status === 'playing';
